import { useEffect } from 'react';
import { useUnityContext } from 'react-unity-webgl';
import { getString } from 'shared-base';
import { bridge } from '../utils/bridge';
import { modelResolveDone, setRadius, setUnityContext, UnityContextHook } from '../utils/globals';

// Name of the Unity build directory
const BUILD_NAME = 'web';

const allowCallbacks: Record<string, boolean> = {
  progress: true,
  UpdateWackModifiers: true,
  UpdateEditorState: true,
  SaveDataExported: true,
  ProjectObjectLoaded: true,
  PaletteValuesResponse: true,
  OnSceneChanged: true,
};

const resolves: Record<string, any> = {
  UpdateWackModifiers: null,
  UpdateEditorState: null,
};

export function getBoardState() {
  return new Promise((resolve) => {
    resolves['UpdateEditorState'] = resolve;
  });
}

export function getWackModifiers() {
  return new Promise((resolve) => {
    resolves['UpdateWackModifiers'] = resolve;
  });
}

type UnityOptions = {
  buildUrl: string;
  unityVersion: UnityVersion;
};

// Hook to inject the UnityContext into the global state
export function useUnity(
  instanceId: string,
  options: UnityOptions,
  handleUnityContext: any = () => {}
) {
  const { buildUrl, unityVersion } = options;
  const version = getString('version');

  const context = useUnityContext({
    loaderUrl: `${buildUrl}${unityVersion}/${BUILD_NAME}.loader.js?${version}`,
    dataUrl: `${buildUrl}${unityVersion}/${BUILD_NAME}.data.unityweb?${version}`,
    frameworkUrl: `${buildUrl}${unityVersion}/${BUILD_NAME}.framework.js.unityweb?${version}`,
    codeUrl: `${buildUrl}${unityVersion}/${BUILD_NAME}.wasm.unityweb?${version}`,

    webglContextAttributes: {
      preserveDrawingBuffer: true,
    },
  });

  function onCallback(eventName: string, data?: any) {
    handleUnityContext(instanceId, eventName, data);
  }

  useEffect(() => {
    setUnityContext(context);
    // console.debug('Unity Context injected into global state.');
  }, [context]);

  useEffect(() => {
    const onGlb = (binary: any) => {
      onCallback('UNITY', { verb: 'incomingGlb', data: binary });
    };

    const onModelLoaded = () => {
      modelResolveDone();
      onCallback('UNITY', { verb: 'modelLoaded' });
    };
    const onMaterialLoaded = () => {};

    const onControlMismatch = (key: string, modelName: string, existingKeys: string) => {
      console.log('onControlMismatch', key, modelName, existingKeys);

      onCallback(
        'UNITY/ControlMismatch',
        new Error(`Missing: ${key} in model:${modelName}. ExistingKeys:${existingKeys}`)
      );
    };

    const onCameraFocusAngles = (radius: number, alpha: number, beta: number) => {
      // console.log('onCameraFocusAngles', radius, alpha, beta);
      setRadius(radius);
    };

    const onTextureLoaded = () => {
      onCallback('UNITY/texture/loaded');
    };

    const onMaterialSlotSelected = (message: any) => {
      onCallback('UNITY', { verb: 'materialSlotSelected', data: message });
      bridge.setSlotId(message);
    };

    const onMaterialSlotEnter = (message: any) => {
      // TODO:
      //onCallback('UNITY', { verb: 'materialSlotSelected', data: message });
    };

    const onMaterialSlotExit = (message: any) => {
      // TODO:
      //onCallback('UNITY', { verb: 'materialSlotSelected', data: message });
    };

    const onReportMeshInfo = (message: any) => {
      // console.log('message =>', message);
    };

    const onControlsSelected = (controlsString: string) => {
      const controlsNames = controlsString.split(',');
      controlsNames.forEach((name) => {
        console.log('control name:', name);
      });
      // onCallback('UNITY', { verb: 'materialSlotSelected', data: message });
    };

    const logToJS = (condition: string, stacktrace: string, logType: ReactUnityEventParameter) => {
      switch (logType) {
        case 0:
          onCallback('UNITY/Error', new Error(condition + '\n' + stacktrace));
          break;
        case 1:
          onCallback('UNITY/Assert', new Error(condition + '\n' + stacktrace));
          break;
        case 2:
          onCallback('UNITY/Warning', new Error(condition?.toString()));
          break;
        case 3:
          onCallback('UNITY/Log', new Error(condition?.toString()));
          break;
        case 4:
          onCallback('UNITY/Exception', new Error(condition + '\n' + stacktrace));
          break;
        default:
          break;
      }
    };

    context.addEventListener('LogToJS', logToJS as any);
    context.addEventListener('GetGlbResponse', onGlb);
    context.addEventListener('ModelLoaded', onModelLoaded);
    context.addEventListener('MaterialLoaded', onMaterialLoaded);
    context.addEventListener('TextureLoaded', onTextureLoaded);
    context.addEventListener('MaterialSlotSelected', onMaterialSlotSelected);
    context.addEventListener('MaterialSlotEnter', onMaterialSlotEnter);
    context.addEventListener('MaterialSlotExit', onMaterialSlotExit);
    context.addEventListener('ControlsSelected', onControlsSelected as any);
    context.addEventListener('LogToControlMismatch', onControlMismatch as any);
    context.addEventListener('OnCameraFocusAngles', onCameraFocusAngles as any);
    context.addEventListener('ReportObjectMeshInfo', onReportMeshInfo as any);

    return () => {
      context.removeEventListener('LogToJS', logToJS as any);
      context.removeEventListener('GetGlbResponse', onGlb);
      context.removeEventListener('ModelLoaded', onModelLoaded);
      context.removeEventListener('MaterialLoaded', onMaterialLoaded);
      context.removeEventListener('TextureLoaded', onTextureLoaded);
      context.removeEventListener('MaterialSlotSelected', onMaterialSlotSelected);
      context.removeEventListener('MaterialSlotEnter', onMaterialSlotEnter);
      context.removeEventListener('MaterialSlotExit', onMaterialSlotExit);
      context.removeEventListener('ControlsSelected', onControlsSelected as any);
      context.removeEventListener('LogToControlMismatch', onControlMismatch as any);
      context.removeEventListener('OnCameraFocusAngles', onCameraFocusAngles as any);
      context.addEventListener('ReportObjectMeshInfo', onReportMeshInfo as any);
    };
  }, []);

  return context as UnityContextHook;
}

export const setAllow = (key: string, value: boolean) => {
  allowCallbacks[key] = value;
};

export const allowStep = () => {
  setAllow('UpdateWackModifiers', true);
  setAllow('UpdateEditorState', true);
};
